import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { faAlarmExclamation, faEllipsisV } from '@fortawesome/pro-regular-svg-icons';
import { faAsterisk } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Assignee from './assignee';
import Dropdown from 'components/dropdown';
import Parent from './parent';
import ReparentingMenuItems from 'bundles/crm/parents/reparenting_menu_items';
import Status from './status';
import TaskType from './task_type';
import Tooltip from 'components/tooltip';
import { formatDate } from 'helpers/formatters/date_time_formats';

import type { CheckboxState } from './status';
import type { Attachment, Task } from 'bundles/apex/tasks/types';
import type { MenuOptions } from 'bundles/apex/tasks/types';
import type { Parent as ParentType } from 'bundles/crm/types';

// Completion animation timing (ms): the checkbox pops (anim), springs back (settle), then the
// row collapses out.
const PHASE_MS = { anim: 200, settle: 320 } as const;
const EXIT_MS = 300;

export interface Props {
  active?: boolean;
  addTaskAttachment: (task: Task, attachment: Attachment) => void;
  animateCompletion?: boolean;
  attachmentsPath: string;
  className?: string;
  currentParent?: ParentType;
  deleteTaskAttachment: any;
  features?: {
    dromoTaskImport?: boolean;
    marketingListings?: boolean;
    taskAttachmentExceptions?: boolean;
  };
  menuOptions?: MenuOptions;
  modalOpen?: boolean;
  restoreTask?: (task: Task) => void;
  showParents?: boolean | 'path';
  task: Task;
  onClick?: (task: Task) => void;
  onExited?: (task: Task) => void;
  onReparent?: (task: Task) => void;
  onUpdate: (task: Task) => Promise<Task>;
}

export default function TaskRow({
  active,
  addTaskAttachment,
  animateCompletion,
  attachmentsPath,
  className,
  currentParent,
  deleteTaskAttachment,
  features,
  menuOptions,
  modalOpen,
  restoreTask,
  showParents,
  task,
  onClick,
  onExited,
  onReparent,
  onUpdate,
}: Props) {
  const [exiting, setExiting] = useState(false);
  const [phase, setPhase] = useState<'anim' | 'settle' | null>(null);
  const exitedRef = useRef(false);
  const timeoutsRef = useRef<number[]>([]);

  useEffect(() => () => timeoutsRef.current.forEach(clearTimeout), []);

  // If a completion is undone while the row is still animating out, cancel the pending exit
  // so the restored (now-incomplete) task stays in the list.
  useEffect(() => {
    if (exiting && !task.completed) {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
      exitedRef.current = false;
      setExiting(false);
      setPhase(null);
    }
  }, [exiting, task.completed]);

  const overdue = !task.completed && task.deadline?.date && new Date(task.deadline.date) < new Date();
  const reparentOptions = menuOptions?.parents?.filter(
    option => option.parent.id !== task.parentId || option.parent.type !== task.parentType
  );

  const checkboxState: CheckboxState = phase ?? (task.completed ? 'checked' : 'idle');

  function finishExit() {
    if (!exitedRef.current) {
      exitedRef.current = true;
      onExited?.(task);
    }
  }

  function startCompletionSequence() {
    const schedule = (fn: () => void, ms: number) => {
      timeoutsRef.current.push(window.setTimeout(fn, ms));
    };

    setPhase('anim');
    schedule(() => setPhase('settle'), PHASE_MS.anim);
    schedule(() => setExiting(true), PHASE_MS.anim + PHASE_MS.settle);
    schedule(finishExit, PHASE_MS.anim + PHASE_MS.settle + EXIT_MS + 60);
  }

  async function handleUpdate(updatedTask: Task) {
    const savedTask = await onUpdate(updatedTask);
    if (animateCompletion && onExited && updatedTask.completed && !task.completed) {
      startCompletionSequence();
    }
    return savedTask;
  }

  function handleTransitionEnd(event: React.TransitionEvent<HTMLDivElement>) {
    if (exiting && event.propertyName === 'max-height') {
      finishExit();
    }
  }

  return (
    <div
      className={classNames(
        'apex-tasks-list__task d-flex align-items-start gap-2 clickable py-2 px-1 border-bottom',
        {
          'apex-tasks-list__task--active': active,
          'apex-tasks-list__task--animated': animateCompletion,
          'apex-tasks-list__task--exiting': exiting,
        },
        className
      )}
      onClick={onClick && (() => onClick(task))}
      onTransitionEnd={animateCompletion ? handleTransitionEnd : undefined}
    >
      <div onClick={e => e.stopPropagation()}>
        <Status
          addTaskAttachment={addTaskAttachment}
          attachmentsPath={attachmentsPath}
          deleteTaskAttachment={deleteTaskAttachment}
          features={features}
          restoreTask={restoreTask}
          state={checkboxState}
          task={task}
          onUpdate={handleUpdate}
        />
      </div>

      <div className="d-flex flex-column gap-1 flex-grow-1" style={{ minWidth: 0 }}>
        <div className="d-flex align-items-start gap-2">
          <div
            className={classNames('apex-tasks-list__task-name fw-bold flex-grow-1', {
              'text-decoration-line-through text-muted': task.completed,
            })}
            style={{ minWidth: 0 }}
          >
            <Tooltip content={task.name} onlyOnOverflow>
              <span className="fw-semibold">{task.name}</span>
              {task.required &&
                <Tooltip content="Required for voucher submission">
                  <FontAwesomeIcon className="ms-1 text-danger fs-xs" icon={faAsterisk} />
                </Tooltip>
              }
            </Tooltip>
          </div>
          <div className="flex-shrink-0">
            <Assignee assignee={task.assignee} />
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 text-muted" style={{ minWidth: 0 }}>
          <TaskType task={task} />
          {showParents &&
            <Parent
              currentParent={currentParent}
              modalOpen={modalOpen}
              task={task}
            />
          }
          {(task.deadline && task.deadline.date) && (
            <div
              className={classNames(
                'apex-tasks-list__task-date ms-auto flex-shrink-0 d-flex gap-1 align-items-center',
                { 'text-solid-pink-900 fw-bold': overdue }
              )}
            >
              {overdue && <FontAwesomeIcon icon={faAlarmExclamation} size="sm" />}
              {formatDate(task.deadline.date)}
            </div>
          )}
        </div>
      </div>

      {!!reparentOptions?.length && (
        <div onClick={e => e.stopPropagation()}>
          <Dropdown icon={faEllipsisV}>
            <ReparentingMenuItems
              parentOptions={reparentOptions}
              record={task}
              onReparent={onReparent}
            />
          </Dropdown>
        </div>
      )}
    </div>
  );
}
